import { attendanceRunsOf } from "~/utils/helpers/attendance/run-envelope";
import { resolveStepLabels } from "~/utils/helpers/copilot/analysis";
import { getCopilotErrorPresentation, normalizeCopilotError } from "~/utils/helpers/copilot/errors";
import { isVisibleCopilotPart } from "~/utils/helpers/copilot/message-parts";
import { AttendanceImportOutcomeEnum, AttendanceRunStatusEnum } from "~/utils/enums/attendance-review.enums";
import { CopilotDataPartEnum, CopilotResourceKindEnum, CopilotRoleEnum } from "~/utils/enums/copilot/chat.enums";
import { CopilotErrorCodeEnum } from "~/utils/enums/copilot/errors.enums";
import type { IAttendanceRunEnvelope } from "~/utils/interfaces/attendance-review-interfaces";
import type { ICopilotAnalysis, ICopilotError } from "~/utils/interfaces/copilot-blocks-interfaces";
import type { ICopilotResources, ICopilotUIMessage } from "~/utils/interfaces/copilot-interfaces";

const DATA_ANALYSIS_PART_TYPE = `data-${CopilotDataPartEnum.ANALYSIS}`;
const DATA_ERROR_PART_TYPE = `data-${CopilotDataPartEnum.ERROR}`;

/** Said when the sheet was read but hangs on a session this screen cannot settle. */
const SESSION_MISMATCH_MESSAGE = "Cette feuille d'émargement ne semble pas correspondre à cette session.";
/** Said when the answer declared nothing at all — there is no reason to pass on. */
const NOTHING_DECLARED_MESSAGE = "La feuille d'émargement n'a pas pu être analysée.";

type IAttendanceImportOutcome =
  | { kind: AttendanceImportOutcomeEnum.PROPOSAL; run: IAttendanceRunEnvelope }
  | { kind: AttendanceImportOutcomeEnum.SESSION_CHOICE }
  | { kind: AttendanceImportOutcomeEnum.ERROR; error: ICopilotError }
  | { kind: AttendanceImportOutcomeEnum.NONE };

/** The last failure the assistant streamed, if it named one. */
function streamedErrorOf(messages: ICopilotUIMessage[]): ICopilotError | null {
  for (const message of [...messages].reverse()) {
    for (const part of [...message.parts].reverse()) {
      if (part.type !== DATA_ERROR_PART_TYPE) continue;
      const error = normalizeCopilotError(part);
      if (error.code !== CopilotErrorCodeEnum.MULTI_TIMESLOT_SESSION) return error;
      const resource = message.parts
        .flatMap((entry) => {
          if (entry.type !== `data-${CopilotDataPartEnum.RESOURCES}` || !("data" in entry)) return [];
          const data = entry.data as Partial<ICopilotResources> | null;
          return Array.isArray(data?.items) ? data.items : [];
        })
        .find((item) => item?.kind === CopilotResourceKindEnum.TRAINING_PLAN_SESSION);

      return normalizeCopilotError({ ...error, resource });
    }
  }

  return null;
}

/**
 * What a streamed sheet import has produced so far.
 *
 * Read structurally off the conversation, exactly like the chat's own consumer: the import sends a
 * single message, and the only thing that decides the screen is whether `declareAttendance` came
 * back with an attendance to verify. A run without a proposal is the session-choice gate, which
 * only the conversation can resolve.
 */
export function attendanceImportOutcomeOf(messages: ICopilotUIMessage[]): IAttendanceImportOutcome {
  const latestAnswer = messages.filter((message) => message.role === CopilotRoleEnum.ASSISTANT).at(-1);
  const blockingError = streamedErrorOf(latestAnswer ? [latestAnswer] : []);
  if (blockingError?.code === CopilotErrorCodeEnum.MULTI_TIMESLOT_SESSION) {
    return { kind: AttendanceImportOutcomeEnum.ERROR, error: blockingError };
  }
  const run = attendanceRunsOf(messages).at(-1)?.payload;

  if (run?.status === AttendanceRunStatusEnum.PROPOSAL && run.proposal) {
    return { kind: AttendanceImportOutcomeEnum.PROPOSAL, run };
  }
  if (run) return { kind: AttendanceImportOutcomeEnum.SESSION_CHOICE };

  const error = streamedErrorOf(messages);

  return error ? { kind: AttendanceImportOutcomeEnum.ERROR, error } : { kind: AttendanceImportOutcomeEnum.NONE };
}

/**
 * What the upload screen says when the import ended on anything but a proposal. A failure the
 * assistant named keeps its own wording — it is the only one that knows why.
 */
export function attendanceImportFailureOf(outcome: IAttendanceImportOutcome): string | null {
  switch (outcome.kind) {
    case AttendanceImportOutcomeEnum.PROPOSAL:
      return null;
    case AttendanceImportOutcomeEnum.SESSION_CHOICE:
      return SESSION_MISMATCH_MESSAGE;
    case AttendanceImportOutcomeEnum.ERROR:
      return getCopilotErrorPresentation(outcome.error).message;
    default:
      return NOTHING_DECLARED_MESSAGE;
  }
}

/**
 * The progress checklist the workflow streams — « Recherche de la session », « Analyse de la feuille
 * d'émargement »… — so the upload screen shows what the backend is doing instead of a mute spinner.
 *
 * The very block the conversation paints, labels included: the backend emits one reconciled part per
 * message (`id: "analysis"`), so the last one is the current state, and `resolveStepLabels` turns
 * its step ids into the design's wording exactly as `CopilotBlocks` does.
 */
export function attendanceImportAnalysisOf(messages: ICopilotUIMessage[]): ICopilotAnalysis | null {
  for (const message of [...messages].reverse()) {
    for (const part of [...message.parts].reverse()) {
      if (part.type !== DATA_ANALYSIS_PART_TYPE || !("data" in part) || !isVisibleCopilotPart(part)) continue;

      return resolveStepLabels(part.data as ICopilotAnalysis, null);
    }
  }

  return null;
}
